import type { QueryClient } from '@tanstack/react-query';
import { storyboardSoundEffectsKeys } from '@/hooks/api/useStoryboardSoundEffects';
import type { StoryboardSoundEffectResponse } from '@/services/api/storyboard-resources';
import { useCollaborationHighlightStore } from '@/stores/collaborationHighlightStore';

export function removeDeletedStoryboardSoundEffectFromCache(
  queryClient: QueryClient,
  params: {
    storyboardId: string;
    soundEffectId: string;
  }
): void {
  const { storyboardId, soundEffectId } = params;
  const listKey = storyboardSoundEffectsKeys.list(storyboardId);

  queryClient.setQueryData<StoryboardSoundEffectResponse[]>(listKey, (current = []) =>
    current.filter((item) => item.soundEffectId !== soundEffectId)
  );

  useCollaborationHighlightStore.getState().clearHighlight({
    targetType: 'sound_effect',
    targetId: soundEffectId,
  });
}

export function upsertStoryboardSoundEffectCache(
  queryClient: QueryClient,
  soundEffect: StoryboardSoundEffectResponse
): void {
  const listKey = storyboardSoundEffectsKeys.list(soundEffect.storyboardId);

  queryClient.setQueryData<StoryboardSoundEffectResponse[]>(listKey, (current = []) => {
    if (current.some((item) => item.soundEffectId === soundEffect.soundEffectId)) {
      return current.map((item) =>
        item.soundEffectId === soundEffect.soundEffectId ? soundEffect : item
      );
    }
    return [soundEffect, ...current];
  });
}

export function applyStoryboardSoundEffectDeletedCacheSync(
  queryClient: QueryClient,
  payload: {
    storyboardId?: string | null;
    entityId?: string | null;
  }
): void {
  const storyboardId = payload.storyboardId?.trim();
  const soundEffectId = payload.entityId?.trim();
  if (!storyboardId || !soundEffectId) return;

  removeDeletedStoryboardSoundEffectFromCache(queryClient, { storyboardId, soundEffectId });
}
